/**
 * The Pick'em setup step's draft, its "what is still missing" list, and the
 * `pickemSetup` payload `/pool/create` carries (ISSUE-1303).
 *
 * Stage A contract: the shape and the three functions are what
 * `features/createPool` calls through `poolTypeSetups.ts`. The web builder
 * owns this file; the create flow never changes again when it does. Server
 * resolves, client renders: the league's starting values, whether it has
 * spreads, the rules text and field errors come from `usePickemSetupPreview`,
 * never from a table here.
 */
import type { usePoolCreate } from "@sportsmet/pool-sdk/private";

type typePoolCreateRequest = Parameters<
  ReturnType<typeof usePoolCreate>["mutateAsync"]
>[0];

/** Exactly the `pickemSetup` object `/pool/create` accepts. */
export type typePickemSetupPayload = NonNullable<
  typePoolCreateRequest["pickemSetup"]
>;

/**
 * null means the Commissioner has not chosen yet. The builder seeds these from
 * `usePickemSetupPreview().defaults` when the step opens.
 */
export type typePickemSetupDraft = {
  /**
   * The league the draft was seeded for. A league change in the create flow
   * starts the draft over from that league's own starting values.
   */
  seededLeagueKey: string | null;
  scoringFormat: typePickemSetupPayload["scoringFormat"] | null;
  picksRequiredMode: typePickemSetupPayload["picksRequiredMode"] | null;
  picksRequiredCount: number | null;
  pickBasis: typePickemSetupPayload["pickBasis"] | null;
  tiebreaker: typePickemSetupPayload["tiebreaker"] | null;
  tiePolicy: typePickemSetupPayload["tiePolicy"] | null;
  /** null while the number box is empty. */
  winPoints: number | null;
  lossPoints: number | null;
  roundGrouping: typePickemSetupPayload["roundGrouping"] | null;
  weekStartDay: number | null;
  startDate: string | null;
  endDate: string | null;
  endDateMode: typePickemSetupPayload["endDateMode"];
};

/** What a confidence pool sends for the two hidden point boxes. */
const EMPTY_PICKEM_SETUP_DRAFT_POINTS = { winPoints: 1, lossPoints: 0 };

export const EMPTY_PICKEM_SETUP_DRAFT: typePickemSetupDraft = {
  seededLeagueKey: null,
  scoringFormat: null,
  picksRequiredMode: null,
  picksRequiredCount: null,
  pickBasis: null,
  tiebreaker: null,
  tiePolicy: null,
  winPoints: 1,
  lossPoints: 0,
  roundGrouping: null,
  weekStartDay: null,
  startDate: null,
  endDate: null,
  endDateMode: "tbd",
};

/** Plain-English items the create footer lists while Create is disabled. */
export function pickemSetupMissingItems(
  draft: typePickemSetupDraft,
): readonly string[] {
  const missing: string[] = [];
  if (draft.scoringFormat === null) missing.push("how picks score");
  if (
    draft.picksRequiredMode === null ||
    (draft.picksRequiredMode === "count" && draft.picksRequiredCount === null)
  ) {
    missing.push("how many games to pick");
  }
  if (draft.pickBasis === null) missing.push("how picks are made");
  if (draft.roundGrouping === null) missing.push("how games make a round");
  if (draft.tiebreaker === null) missing.push("the tiebreaker");
  if (draft.tiePolicy === null) missing.push("what a tie counts as");
  // Confidence scoring pays the rank, so the two point boxes are hidden there.
  if (
    draft.scoringFormat !== "confidence" &&
    (draft.winPoints === null || draft.lossPoints === null)
  ) {
    missing.push("the points for each pick");
  }
  if (draft.endDateMode === "date" && draft.endDate === null) {
    missing.push("an end date");
  }
  return missing;
}

/** The `pickemSetup` payload, or null while anything is missing. */
export function buildPickemSetupPayload(
  draft: typePickemSetupDraft,
): typePickemSetupPayload | null {
  if (
    pickemSetupMissingItems(draft).length > 0 ||
    draft.scoringFormat === null ||
    draft.picksRequiredMode === null ||
    draft.pickBasis === null ||
    draft.tiebreaker === null ||
    draft.tiePolicy === null ||
    draft.roundGrouping === null
  ) {
    return null;
  }
  return {
    scoringFormat: draft.scoringFormat,
    picksRequiredMode: draft.picksRequiredMode,
    picksRequiredCount:
      draft.picksRequiredMode === "count" ? draft.picksRequiredCount : null,
    pickBasis: draft.pickBasis,
    tiebreaker: draft.tiebreaker,
    tiePolicy: draft.tiePolicy,
    winPoints: draft.winPoints ?? EMPTY_PICKEM_SETUP_DRAFT_POINTS.winPoints,
    lossPoints: draft.lossPoints ?? EMPTY_PICKEM_SETUP_DRAFT_POINTS.lossPoints,
    roundGrouping: draft.roundGrouping,
    weekStartDay: draft.roundGrouping === "week" ? draft.weekStartDay : null,
    startDate: draft.startDate,
    endDate: draft.endDateMode === "date" ? draft.endDate : null,
    endDateMode: draft.endDateMode,
  };
}

/**
 * A draft holding exactly the values of a full setup: the league's starting
 * values from `usePickemSetupPreview`, or a pool's current settings from
 * `usePickemPoolGet`. Extra fields on the source are ignored.
 */
export function pickemDraftFromSetup(input: {
  setup: typePickemSetupPayload;
  leagueKey: string | null;
}): typePickemSetupDraft {
  const setup = input.setup;
  return {
    seededLeagueKey: input.leagueKey,
    scoringFormat: setup.scoringFormat,
    picksRequiredMode: setup.picksRequiredMode,
    picksRequiredCount: setup.picksRequiredCount ?? null,
    pickBasis: setup.pickBasis,
    tiebreaker: setup.tiebreaker,
    tiePolicy: setup.tiePolicy,
    winPoints: setup.winPoints,
    lossPoints: setup.lossPoints,
    roundGrouping: setup.roundGrouping,
    weekStartDay: setup.weekStartDay ?? null,
    startDate: setup.startDate ?? null,
    endDate: setup.endDate ?? null,
    endDateMode: setup.endDateMode,
  };
}

/** True while the draft still needs the league's starting values. */
export function needsPickemSeed(input: {
  draft: typePickemSetupDraft;
  leagueKey: string | null;
}): boolean {
  return (
    input.leagueKey !== null && input.draft.seededLeagueKey !== input.leagueKey
  );
}

/** Whether two drafts would save the same settings. */
export function isSamePickemSetup(
  left: typePickemSetupDraft,
  right: typePickemSetupDraft,
): boolean {
  return (
    JSON.stringify(buildComparable(left)) ===
    JSON.stringify(buildComparable(right))
  );
}

function buildComparable(draft: typePickemSetupDraft): unknown[] {
  return [
    draft.scoringFormat,
    draft.picksRequiredMode,
    draft.picksRequiredMode === "count" ? draft.picksRequiredCount : null,
    draft.pickBasis,
    draft.tiebreaker,
    draft.tiePolicy,
    draft.winPoints,
    draft.lossPoints,
    draft.roundGrouping,
    draft.roundGrouping === "week" ? draft.weekStartDay : null,
    draft.startDate,
    draft.endDateMode === "date" ? draft.endDate : null,
    draft.endDateMode,
  ];
}
